import * as React from 'react';
import './Billing.scss';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import { ButtonGroup, Button, Alert, Modal, ModalBody, ModalHeader, ListGroup, ListGroupItem } from 'reactstrap';
import ProfessionalSalaryModal from './ProfessionalSalaryModal';
import StyledTable from '../../components/StyledTable';
import { AppointmentData, ExpenseData, ProfessionalData } from '../../models';
import { useEntitiesData } from '../../store/entities/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingEntities } from '../../store/entities/selectors';
import { entityCreating, loadEntities, openAppointment } from '../../store/entities/actions';
import { getAppointmentsAsync } from '../../ajax/requests';
import Typography from '../../components/Typography';
import { hide } from '../../shared/style';
import { formatEuros } from '../../shared/string';
import { BillingResponsiveBar } from './BillingResponsiveBar';
import { getExtrasSettings, getPaymentsSettings } from '../../store/settings/selectors';
import { getTotalForProfessional } from '../../shared/appointmint/payments';
import { cookiesOptions, Guid } from '../../shared/utils';
import { useCallback, useMemo, useState } from 'react';
import { getActualTotalPrice, serviceName } from '../../shared/appointmint/appointment';
import { useGeneralSettings } from '../../store/settings/hooks';
import { CSVLink } from 'react-csv';
import { useCookies } from 'react-cookie';
import { DateRangeSelector, DateRangeSelectorState } from '../../components/DateRangeSelector';
import { SwitchField } from '../../components/Forms/fields';
import { AppointmentsFilters } from './AppointmentsFIlters';
import { BillItem, DataTotals, Show, TableDataItem } from './billing-worker';
import moment from 'moment';
import { useBillingGroupBy } from '../../hooks/useBillingGroupBy';
import { TotalsGrid, StatCard, CardLabel, CardValue, CardSubValue } from './BillingStatCards';
import { CsvRow, toCsvValue } from '../../shared/csv';

const average = (array: number[]) => (array.reduce((a, b) => a + b, 0) / array.length).toFixed(1)

export const buildAppointmentsCsvData = (rows: TableDataItem[]): CsvRow[] => rows.map(row => ({...row}))

export const buildBillingCsvData = (
    rows: BillItem[],
    columns: string[],
    appointmentsKey: string,
): CsvRow[] => rows.map(({label, values}) => ({
    label,
    [appointmentsKey]: toCsvValue(values[appointmentsKey]),
    ...Object.fromEntries(columns.map(column => [column, toCsvValue(values[column])])),
}))

const Billing: React.FC = () => {
 
    const dispatch = useDispatch()
    const t = useTranslations()
    const generalSettings = useGeneralSettings()

    const [appointmentsToShow, showAppointments] = useState<AppointmentData[]>([])
    const [professionalSalaryToShow, showProfessionalSalary] = useState<BillItem|undefined>()
    const [appointments, setAppointments] = useState<AppointmentData[]>([])

    const [show] = useState<Show>({
        paidNotPaid: false,
        extras: false,
        duration: false,
    })

    const [excludeZeroValues, setExcludeZeroValues] = React.useState<boolean>(false)

    const professionalsData = useEntitiesData<ProfessionalData>('professionals')
    const professionals = useMemo(() => Object.values(professionalsData.items).filter((p): p is ProfessionalData => !!p), [professionalsData])

    const isLoading = [
        useSelector(isLoadingEntities('appointments')),
        useSelector(isLoadingEntities('professionals')),
    ].some(v => v)

    const _paymentProfiles = useSelector(getPaymentsSettings)
    const paymentProfiles = useMemo(() => Object.fromEntries(_paymentProfiles.map(p => [p.id, p])), [_paymentProfiles])

    const _extrasConfig = useSelector(getExtrasSettings)
    const extrasConfig = useMemo(() => Object.fromEntries(_extrasConfig.map(p => [p.id, p])), [_extrasConfig])

    const [cookies, setCookie] = useCookies()

    const [showWarningMsg, setShowWarningMsg] = React.useState(!cookies['BILLING_NOTE'])
    const closeWarningMsg = React.useCallback(() => {
        setShowWarningMsg(false)
        setCookie('BILLING_NOTE', 'SHOWN', cookiesOptions)
    }, [setShowWarningMsg, setCookie])

    const [splitBy, setSplitBy] = React.useState<'day'|'professional'|'appointmentType'|'client'>('day')
    const [groupBy, setGroupBy] = useBillingGroupBy()
    const [dontGroup, setDontGroup] = React.useState<boolean>(!!cookies['BILLING_DONT_GROUP'])
    const [window, setWindow] = React.useState<DateRangeSelectorState | undefined>()

    const [barType, setBarType] = React.useState<'sum'|'count'>('sum')
    const [professionalBar, setProfessionalBar] = React.useState<'totals'|'margin'>('totals')

    const allAppointmentsTypes = React.useMemo(() => [...new Set(appointments.map(a => a.type))], [appointments])
    const capabilitiesSet = allAppointmentsTypes
    const typeNameMap = useMemo(() =>
        Object.fromEntries(allAppointmentsTypes.map(k => [k, serviceName(k, generalSettings)])),
        [allAppointmentsTypes, generalSettings]
    )

    const filteredClients = React.useMemo(() => [...new Set(appointments.map(a => a.clientName))], [appointments])
    const filteredProfessionals = React.useMemo(() => [...new Set(appointments.map(a => a.professionalName))], [appointments])
    
    const getAppointmentsController = React.useRef<AbortController>()

    React.useEffect(() => {
        if (window) {
            if (getAppointmentsController.current) {
                getAppointmentsController.current.abort()
            }
            getAppointmentsController.current = new AbortController()
            const signal = getAppointmentsController.current.signal

            const diff = moment.utc(window.to).diff(moment.utc(window.from), 'months')
            setGroupBy(diff <= 1 ? 'byDay' : 'byMonth')

            dispatch(loadEntities('appointments', () => getAppointmentsAsync(window.from, window.to, signal), 'NewResult', e => e.date >= window.from && e.date <= window.to))
        }
    }, [window])
    
    const professionalsPayments = useMemo<Record<string, [number, string[]]>>(() => 
        Object.fromEntries(professionals.map(p => [p.id, getTotalForProfessional(appointments, p, paymentProfiles, extrasConfig)])),
        [professionals, appointments])


    const [dataSplit, setDataSplit] = React.useState<BillItem[]>([])
    const [totals, setTotals] = React.useState<DataTotals>({notPaid: {sum: 0, count: 0}, paid: {sum: 0, count: 0}, paidWithPacks: {sum: 0, count: 0}, total: {sum: 0, count: 0}, professionals: 0 })
    const [allData, setAllData] = React.useState<TableDataItem[]>([])
    const [isComputing, setIsComputing] = React.useState(false)
    const workerRef = React.useRef<Worker>()

    React.useEffect(() => {
        if (window) {
            if (workerRef.current) {
                workerRef.current.terminate()
            }

            setIsComputing(true)

            const worker = new Worker(new URL('./billing-worker.ts', import.meta.url), { type: 'module' })
            workerRef.current = worker
            worker.postMessage({
                splitBy,
                groupBy,
                appointments,
                professionalsPayments,
                capabilitiesSet,
                typeNameMap,
                extrasConfig,
                show,
                window,
                excludeZeroValues,
            })
            worker.onmessage = function(e: MessageEvent<[BillItem[], TableDataItem[], DataTotals]>) {
                if (workerRef.current !== worker) return // ignore old workers
                setDataSplit(e.data[0].map(({data, label, values}) => ({data, label, values: Object.fromEntries(Object.entries(values).map(([k, v]) => [t(k as TK), v]))})))
                setAllData(e.data[1])
                setTotals(e.data[2])
                worker.terminate()
                setIsComputing(false)
            }
            return () => worker.terminate()
        }
    }, [t, splitBy, groupBy, appointments, professionalsPayments, capabilitiesSet, typeNameMap, extrasConfig, show, window, excludeZeroValues])

    const onClick = React.useCallback((appointmentId: string) => () => {
        dispatch(openAppointment(appointmentId))
    }, [dispatch])

    const onCreateExpenseForProfessionalSalary = React.useCallback(() => {
        if (!professionalSalaryToShow || !window) return

        const {from, to} = window
        const {label: name, values} = professionalSalaryToShow
        const value = values[t(TK.professional)]
        const professionalSalary = !value || typeof value === 'string' ? 0
            : typeof value === 'number' ? value
            : value.sum

        const professional = professionals.find(p => p.name === name)

        dispatch(entityCreating<ExpenseData>('expenses', {
            id: Guid.newGuid(),
            entity: t(TK.professional),
            name: `${t(TK.salary)} - ${name}`,
            notes: `${t(TK.from)} ${from} - ${to}`,
            type: t(TK.salary),
            payment: {value: professionalSalary, type: '', date: new Date().toISOString()},
            attachments: [],
            professionalId: professional?.id,
            professionalName: professional?.name,
        }))
    }, [dispatch, professionalSalaryToShow, professionals])

    const columns = React.useMemo(
        () => [...new Set(dataSplit.flatMap(d => Object.keys(d.values)))].filter(c => c !== t(TK.appointments)), // dont show appointments count but keep it for the CSV export
        [dataSplit]);
    
    const openAppointmentCallback = useCallback((a: AppointmentData) => () => dispatch(openAppointment(a.id)), [])

    const barData = useMemo(() => dataSplit.map(({label, values}) => ({label, ...values})), [dataSplit])
    const barGroupMode = useMemo(() => splitBy === 'professional' ? 'grouped' : 'stacked', [splitBy])  
    const capabilitiesDisplayNames = useMemo(() => capabilitiesSet.map(k => typeNameMap[k] ?? k), [capabilitiesSet, typeNameMap])
    const barKeys = useMemo(() =>
        splitBy === 'day' ? show.paidNotPaid ? [t(TK.notPaid), t(TK.paid)] : capabilitiesDisplayNames
            : splitBy === 'professional' && professionalBar === 'totals' ? [t(TK.professional), t(TK.total)]
            : splitBy === 'professional' && professionalBar === 'margin' ? [t(TK.operatingMargin), t(TK.total)]
            : splitBy === 'client' ? capabilitiesDisplayNames
            : [t(TK.total)],
        [splitBy, professionalBar, show, capabilitiesDisplayNames],
    )

    const appointmentsCsvHeaders = useMemo(() => [
        {label: t(TK.date), key: 'date'},
        {label: t(TK.duration), key: 'duration'},
        {label: t(TK.client), key: 'client'},
        {label: t(TK.professional), key: 'professional'},
        {label: t(TK.appointmentType), key: 'type'},
        {label: t(TK.total), key: 'total'},
        {label: t(TK.extras), key: 'extras'},
        {label: t(TK.notPaid), key: 'notPaid'},
        {label: t(TK.professionalParcel), key: 'professionalPart'},
    ], [t])

    const billingLabelHeader = useMemo(() =>
        splitBy === 'day' ? t(TK.date)
            : splitBy === 'professional' ? t(TK.professional)
            : splitBy === 'appointmentType' ? t(TK.appointmentType)
            : t(TK.client),
        [splitBy, t],
    )

    const billingAppointmentsLabel = t(TK.appointments)
    const billingCsvHeaders = useMemo(() => [
        {label: billingLabelHeader, key: 'label'},
        {label: billingAppointmentsLabel, key: 'appointments'},
        ...columns.map(column => ({label: column, key: column})),
    ], [billingAppointmentsLabel, billingLabelHeader, columns])

    const appointmentsCsvData = useMemo(() => buildAppointmentsCsvData(allData), [allData])
    const billingCsvData = useMemo(
        () => buildBillingCsvData(dataSplit, columns, 'appointments'),
        [columns, dataSplit],
    )

    return (
        <div className="billingContainer">
            {(isLoading || isComputing) && <Typography style={{position: 'fixed', bottom: 0, left: 0, backgroundColor: 'white'}}>{t(TK.loading)}...</Typography>}

            <Alert color="secondary" isOpen={showWarningMsg} toggle={closeWarningMsg}>
                <div dangerouslySetInnerHTML={{__html: t(TK.appointmentsBillingDescription)}}/>
            </Alert>

            <DateRangeSelector onRangeChanged={setWindow} />

            <div className="billTypePanel">
                <ButtonGroup >
                    <Button outline color="info" active={splitBy === 'day'} onClick={() => setSplitBy('day')}>{t(TK.byDate)}</Button>
                    <Button outline color="info" active={splitBy === 'professional'} onClick={() => setSplitBy('professional')}>{t(TK.byProfessional)}</Button>
                    <Button outline color="info" active={splitBy === 'appointmentType'} onClick={() => setSplitBy('appointmentType')}>{t(TK.byAppointmentType)}</Button>
                    <Button outline color="info" active={splitBy === 'client'} onClick={() => setSplitBy('client')}>{t(TK.byClient)}</Button>
                </ButtonGroup>
                {splitBy === 'day' && <ButtonGroup >
                    <Button outline color="info" active={groupBy === 'byDay'} onClick={() => setGroupBy('byDay')}>{t(TK.byDay)}</Button>
                    <Button outline color="info" active={groupBy === 'byWeek'} onClick={() => setGroupBy('byWeek')}>{t(TK.byWeek)}</Button>
                    <Button outline color="info" active={groupBy === 'byMonth'} onClick={() => setGroupBy('byMonth')}>{t(TK.byMonth)}</Button>
                </ButtonGroup>}
                <AppointmentsFilters 
                    onAppointmentsFiltered={a => setAppointments(excludeZeroValues ? a.filter(a => getActualTotalPrice(a) > 0) : a)} 
                />
            </div>


            { isLoading ? <Typography>{t(TK.loading)}...</Typography> 
                : isComputing ? <Typography>{t(TK.calculating)}...</Typography> 
                : <>
                    <TotalsGrid style={{marginTop: 'var(--spacer-M)'}}>
                        <StatCard accent="var(--success-color)">
                            <CardLabel>{t(TK.total)}</CardLabel>
                            <CardValue textColor="var(--success-color)">{formatEuros(totals.total)}</CardValue>
                            <CardSubValue>{appointments.length} {t(TK.appointments).toLowerCase()}</CardSubValue>
                            <CardSubValue>{filteredClients.length} {t(TK.clients).toLowerCase()}</CardSubValue>
                            <CardSubValue>{filteredProfessionals.length} {t(TK.professionals).toLowerCase()}</CardSubValue>
                        </StatCard>
                        {show.paidNotPaid && (
                            <StatCard accent="var(--appGreen)">
                                <CardLabel>{t(TK.paid)}</CardLabel>
                                <CardValue textColor="var(--appGreen)">{formatEuros(totals.paid)}</CardValue>
                                {!!totals.paidWithPacks && <CardSubValue>Packs: {formatEuros(totals.paidWithPacks)}</CardSubValue>}
                            </StatCard>
                        )}
                        {show.paidNotPaid && (
                            <StatCard accent="var(--danger-color)">
                                <CardLabel>{t(TK.notPaid)}</CardLabel>
                                <CardValue textColor="var(--danger-color)">{formatEuros(totals.notPaid)}</CardValue>
                            </StatCard>
                        )}
                        {splitBy === 'professional' && (
                            <StatCard accent="var(--info-color)">
                                <CardLabel>{t(TK.professionals)}</CardLabel>
                                <CardValue textColor="var(--info-color)">{formatEuros(totals.professionals)}</CardValue>
                            </StatCard>
                        )}
                        {(splitBy === 'professional' || splitBy === 'client') && (
                            <StatCard accent="var(--appLigthGrey)">
                                <CardLabel>{t(TK.average)}</CardLabel>
                                <CardValue textColor="var(--text-body)">{formatEuros(totals.total.sum / totals.total.count)}</CardValue>
                                <CardSubValue>{average(dataSplit.map(d => d.data.length))} {t(TK.appointments).toLowerCase()}</CardSubValue>
                            </StatCard>
                        )}
                    </TotalsGrid>
                    
                    <BillingResponsiveBar
                        data={barData}
                        type={barType}
                        keys={barKeys}
                        groupMode={barGroupMode}
                        onClick={(id, item) => splitBy === 'day' ? showAppointments(dataSplit.find(d => d.label === item.label)?.data.filter(d => d.type === id) ?? [])
                            : splitBy === 'client' ? showAppointments(dataSplit.find(d => d.label === item.label)?.data.filter(d => d.type === id) ?? [])
                            : splitBy === 'appointmentType' ? showAppointments(dataSplit.find(d => d.label === item.label)?.data ?? [])
                            : splitBy === 'professional' ? showProfessionalSalary(dataSplit.find(d => d.label === item.label))
                            : undefined}
                    />

                    <div style={{display: 'flex', gap: 'var(--spacer-XS)', justifyContent: 'end', position: 'relative', top: '-30px'}}>
                        <span>{t(TK.showCount)}</span>
                        <SwitchField 
                            style={{display: 'inline'}}
                            value={barType === 'count'}
                            id='show-count'
                            onChange={e => setBarType(e ? 'count' : 'sum')}
                        />
                    </div>

                    {splitBy === 'professional' && 
                        <div style={{display: 'flex', gap: 'var(--spacer-XS)', justifyContent: 'end', position: 'relative', top: '-30px'}}>
                            <span>{t(TK.operatingMargin)}</span>
                            <SwitchField 
                                style={{display: 'inline'}}
                                value={professionalBar === 'margin'}
                                id='show-margin'
                                onChange={e => setProfessionalBar(e ? 'margin' : 'totals')}
                            />
                        </div>
                    }

                    <div style={{display: 'flex', gap: 'var(--spacer-XS)', justifyContent: 'end', position: 'relative', top: '-30px'}}>
                        <span>{t(TK.excludeZeroValueAppointments)}</span>
                        <SwitchField 
                            style={{display: 'inline'}}
                            value={excludeZeroValues}
                            id='exclude-zero-values'
                            onChange={e => setExcludeZeroValues(!!e)}
                        />
                    </div>

                    <div style={{display: 'flex', gap: 'var(--spacer-XS)', justifyContent: 'end', position: 'relative', top: '-30px'}}>
                        <span>{t(TK.dontGroup)}</span>
                        <SwitchField
                            style={{display: 'inline'}}
                            value={dontGroup}
                            id='dont-group'
                            onChange={e => {
                                const val = !!e
                                setDontGroup(val)
                                setCookie('BILLING_DONT_GROUP', val ? '1' : '', cookiesOptions)
                            }}
                        />
                    </div>
        
                
                    <div className="billingTable" style={hide(!dontGroup)}>
                        <StyledTable scrollable>
                            <thead>
                                <tr>
                                    <th>{t(TK.date)}</th>
                                    <th>{t(TK.client)}</th>
                                    <th>{t(TK.professional)}</th>
                                    <th>{t(TK.appointmentType)}</th>
                                    <th>{t(TK.total)}</th>
                                    {show.extras && <th>{t(TK.extras)}</th>}
                                    {show.paidNotPaid && <th>{t(TK.notPaid)}</th>}
                                    <th>{t(TK.payment)}</th>
                                    <th>{t(TK.professional)}</th>
                                </tr>
                            </thead>
                            <tbody>
                            {allData.map(p => (
                                <tr key={p.id}>
                                    <td>{p.date}</td>
                                    <td><Button size="sm" color="link" onClick={onClick(p.id)}>{p.client}</Button></td>
                                    <td>{p.professional}</td>
                                    <td>{p.type}</td>
                                    <td>{p.total}</td>
                                    {show.extras && <td>{p.extras}</td>}
                                    {show.paidNotPaid && <td>{p.notPaid}</td>}
                                    <td style={{color: p.paymentStatus === TK.notPaid ? 'var(--danger-color)' : 'var(--success-color)'}}>{t(p.paymentStatus)}</td>
                                    <td>{p.professionalPart}</td>
                                </tr>
                            ))}
                            </tbody>
                        </StyledTable>
                    </div>

                    <div className="billingTable" style={hide(dontGroup)}>
                        <StyledTable>
                            <thead>
                                <tr>
                                    <th/>
                                    { columns.map(column => <th key={column}>{column}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                { dataSplit.map((item, i) =>
                                    <tr key={`${item.label}-${i}`} onClick={() => splitBy === 'professional' ? showProfessionalSalary(item) : showAppointments(item.data)} style={{cursor: 'pointer'}}>
                                        <td>{item.label}</td>
                                        { columns.map(column =>
                                            <td key={column}>{formatEuros(item.values[column])}</td>
                                        )}
                                    </tr>
                                )}
                            </tbody>
                        </StyledTable>
                    </div>

                    <CSVLink 
                        headers={appointmentsCsvHeaders}
                        data={appointmentsCsvData}
                        separator=';' 
                        uFEFF
                        target='_blank'
                        filename='appointments-export.csv'
                    >
                        {t(TK.exportData)} ({t(TK.all)})
                    </CSVLink>

                    <CSVLink 
                        headers={billingCsvHeaders}
                        data={billingCsvData}
                        separator=';' 
                        uFEFF
                        target='_blank'
                        filename='billing-export.csv'
                    >
                        {t(TK.exportData)} ({t(TK.filter)})
                    </CSVLink>
                </>
            }
            
            <Modal size='lg' isOpen={!!appointmentsToShow.length} toggle={() => showAppointments([])}>
                <ModalHeader toggle={() =>  showAppointments([])}>{t(TK.appointments)}</ModalHeader>
                    <ModalBody>
                        <ListGroup>
                            {appointmentsToShow.map(a => (
                                <ListGroupItem key={a.id}>
                                    <Button color='link' style={{padding: 0, verticalAlign: 'top'}} onClick={openAppointmentCallback(a)}>{a.date} {a.start}</Button>
                                    &nbsp;- <b>{a.clientName}</b> ({serviceName(a.type, generalSettings)})
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                </ModalBody>
            </Modal>

            <ProfessionalSalaryModal
                isOpen={!!professionalSalaryToShow}
                onClose={() => showProfessionalSalary(undefined)}
                professionalName={professionalSalaryToShow?.label ?? ''}
                professionalParcel={(professionalSalaryToShow?.values[t(TK.professional)] as any)?.sum ?? 0}
                totalProduced={(professionalSalaryToShow?.values[t(TK.total)] as any)?.sum ?? 0}
                summary={(professionalSalaryToShow?.values[t(TK.summary)] as string ?? '').split(' + ')}
                appointments={professionalSalaryToShow?.data ?? []}
                onCreateExpense={onCreateExpenseForProfessionalSalary}
            />
        </div>
    );
}

export default Billing;
